import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  forwardRef,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
  ViewChild,
  Renderer2,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { KeyboardKeys } from '../../../helpers/KeyboardKeys.enum';

import { PhxDomainConfig, PhxDomainOptions, PhxOmniOption, PhxSelectedOmniOptionsByDomain } from '../phx-omni-search-filter.types';
import { Bullet20, ChevronUpDown, Cross, MagnifyingGlass } from '../../icon/icon-map';
import { PhxSelectionListComponent } from '../../select/selection-list.component';
import { PhxOmnifilterFocusableDirective } from './phx-omnifilter-focusable.directive';
import { PhxTranslatePipe } from '../../../translation/phx-translation.pipe';
import { PhxSelectOption } from '../../select/select.types';
import { phxWebStyleTokens } from '../../../styles/phx-tokens';
import { PhxOmniBaseComponent } from '../phx-omni-base.component';
import { PhxContextService } from '../../../services/phx-context-service';
import { PhxOmnifilterContextService } from './phx-omnifilter-context.service';

const QUICK_SELECT_GROUP_LABEL = 'Quick Select';

@Component({
  selector: 'phx-omnifilter',
  templateUrl: './phx-omnifilter.component.html',
  styleUrls: ['./phx-omnifilter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxOmnifilterComponent),
      multi: true,
    },
    { provide: PhxContextService, useClass: PhxOmnifilterContextService },
  ],
  host: {
    class: 'phx-omnifilter',
    role: 'menu',
    '[class.phx-omnifilter-disabled]': 'phxDisabled',
    '[class.phx-omnifilter-searched-selected]': '!phxDisabled && !phxMultiple && combinedTokens.length > 0',
    '[class.phx-omnifilter-no-right-padding]': 'phxMultiple && combinedTokens.length > 0',
    '(keydown)': 'onHostKeydown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxOmnifilterComponent extends PhxOmniBaseComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  // Base component provides: phxDomains, phxPlaceholder, phxDebounceTime, phxDisabled, phxInputValue, phxShowByType, phxFlattenedOptions, phxGroupedOptions, phxLoading
  @Input() phxDefaultDomain: PhxDomainOptions = PhxDomainOptions.ALL;
  @Input() phxMode: boolean = true;
  @Input() phxPlaceholder: string = 'Search all domains'; // Override base default
  @Input() phxShowByType: boolean = true; // Override base default
  @Input() phxShowDeactivatedEntities: boolean = false;
  @Input() phxShowDeactivatedToggle: boolean = true;
  @Input() phxEnablePredictive: boolean = true;
  @Input() phxRequired: boolean = false;
  @Input() phxShowFindByKeyword: boolean = false;
  @Input() phxSingleSelectPerDomain: boolean = false;
  @Input() phxMultiple: boolean = false;
  @Input() phxSelectedQuickOptions: PhxOmniOption[] = [];
  @Input() phxNavigateOnEntityClick: boolean = false;

  // Base component provides: phxSearch, phxScrollEnd, phxOpenChange, phxClearAll, phxShowByTypeChange
  @Output() phxQuickSelectionsChange: EventEmitter<PhxOmniOption[]> = new EventEmitter<PhxOmniOption[]>();
  @Output() phxTokenRemove: EventEmitter<PhxOmniOption> = new EventEmitter<PhxOmniOption>();
  @Output() phxFindByKeywordClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() phxDomainChange: EventEmitter<PhxDomainOptions> = new EventEmitter<PhxDomainOptions>();
  @Output() phxEntityClick: EventEmitter<PhxOmniOption> = new EventEmitter<PhxOmniOption>();
  @Output() phxShowDeactivatedEntitiesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('omnifilterInput', { read: ElementRef }) omnifilterInput: ElementRef;
  @ViewChild('dropdownTrigger', { read: ElementRef }) dropdownTrigger: ElementRef;
  @ViewChild('dropdownContainer', { read: ElementRef, static: false }) dropdownContainer: ElementRef;
  @ViewChild(PhxSelectionListComponent) phxSelectionList: PhxSelectionListComponent;
  @ViewChildren(PhxOmnifilterFocusableDirective) focusableItems: QueryList<PhxOmnifilterFocusableDirective>;

  private _onChange: (value: PhxSelectedOmniOptionsByDomain) => void = () => { };
  private _onTouched: () => void = () => { };
  private _tooltipContent: string = '';
  private _cachedPlaceholderText: string = '';
  private _cachedFormattedOptions: PhxSelectOption[] = [];
  private _cachedQuickOptions: PhxSelectOption[] = [];
  // Base component provides: _cachedFindByString, _cachedNoResultsMessage, destroy$, _activeFocusElement, phxOptions, shouldGroupOptions, domainList

  public isDropdownOpen: boolean = false;
  public isOpen: boolean = false;
  public inputValue: string = '';
  public selectedOmniOptions: PhxOmniOption[] = [];
  public searchQuery: string = '';
  public selectedDomain: PhxDomainConfig;
  public selectedDomainItem: PhxDomainOptions;
  public selectedOptions: PhxSelectOption[] = [];
  public selectedQuickOptions: PhxSelectOption[] = [];
  public combinedTokens: PhxOmniOption[] = [];
  public quickOptions: PhxSelectOption[] = [];
  public triggerWidth: number = 0;
  public deactivatedEntitesMessage: string = '';

  public readonly MagnifyingGlass: string = MagnifyingGlass;
  public readonly BulletIcon: string = Bullet20;
  public readonly ChevronUpDown: string = ChevronUpDown;
  public readonly CrossIcon: string = Cross;
  public readonly CrossIconBadge: string = phxWebStyleTokens.colorContentOnBold;
  public readonly MaximumTooltipOptionsToShow: number = 8;
  public isKeywordHighlighted: boolean = true;

  public get tooltipContent(): string {
    return this._tooltipContent;
  }

  public get placeholderText(): string {
    return this.selectedDomain?.placeholder || this._cachedPlaceholderText;
  }

  constructor(renderer: Renderer2, cdr: ChangeDetectorRef, phxTranslate: PhxTranslatePipe) {
    super(renderer, cdr, phxTranslate);
    this.setDerivedComponentName('PhxOmnifilterComponent');
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Call base component ngOnChanges first
    super.ngOnChanges(changes);

    const { phxDomains, phxDefaultDomain, phxSelectedQuickOptions, phxFlattenedOptions, phxGroupedOptions } = changes;

    if (phxDomains && phxDomains.currentValue) {
      if (Object.keys(this.phxDomains).length > 1) {
        this.selectedDomain = this.phxDomains[this.phxDefaultDomain];
      } else {
        this.selectedDomain = Object.values(this.phxDomains)[0];
      }
      this.selectedDomainItem = this.selectedDomain.name;
      this.quickOptions = this.getQuickOptions();
      this.updateCachedMessages();
    }

    if (phxDefaultDomain && phxDefaultDomain.currentValue) {
      this.selectedDomain = this.phxDomains[phxDefaultDomain.currentValue];
      this.selectedDomainItem = this.selectedDomain.name;
      this.quickOptions = this.getQuickOptions();
    }

    if (phxSelectedQuickOptions) {
      if (this.phxSelectedQuickOptions) {
        this.selectedQuickOptions = phxSelectedQuickOptions.currentValue?.map(this.transformToPhxSelectOption.bind(this)) || [];
      } else {
        this.selectedQuickOptions = [];
      }
      this.updateCombinedSelections(false);
      this.filterSelectedOptions();
    }

    // Process options when data changes
    if (phxFlattenedOptions || phxGroupedOptions) {
      this.formatOptions();
      this.filterSelectedOptions();
      this._cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.boundHandleOutsideClick, true);
  }

  public writeValue(value: PhxSelectedOmniOptionsByDomain | null): void {
    if (!value) {
      this.selectedOptions = [];
      this.selectedOmniOptions = [];
      this.updateCombinedSelections(false);
      this.onSearchClear();
      this._cdr.markForCheck();
      return;
    }

    const flattenedValues: PhxOmniOption[] = this.convertToOmniOption(value);

    this.selectedOptions = flattenedValues.map(this.transformToPhxSelectOption.bind(this));
    this.selectedOmniOptions = flattenedValues;
    this.updateCombinedSelections(false);

    if (!this.hasInput && !this.phxMultiple && this.combinedTokens?.length > 0) {
      this.updateInputValue(this.combinedTokens[0]?.label || '');
    }

    this.updateTooltipContent();
    this.updateCachedMessages();
    this._cdr.markForCheck();
  }

  public onFocus(): void {
    this.openDropdown();
    this._onTouched();
    document.addEventListener('click', this.boundHandleOutsideClick, true);
  }

  private isSelectedQuickOption(option: PhxOmniOption): boolean {
    return this.phxSelectedQuickOptions.some((opt) => opt.value === option.value);
  }

  private handleOutsideClick(event: MouseEvent): void {
    const triggerElement: HTMLElement = this.dropdownTrigger?.nativeElement;
    const dropdownElement: HTMLElement = this.dropdownContainer?.nativeElement;

    const isOutsideTrigger: boolean = triggerElement && !triggerElement.contains(event.target as Node);
    const isOutsideDropdown: boolean = dropdownElement && !dropdownElement.contains(event.target as Node);

    if (isOutsideTrigger && isOutsideDropdown) {
      this.isDropdownOpen = false;
      this._cdr.detectChanges();
      document.removeEventListener('click', this.boundHandleOutsideClick, true);
    }
  }

  private boundHandleOutsideClick = this.handleOutsideClick.bind(this);

  // Override handleInput to add domain-specific logic
  public handleInput(event: Event): void {
    super.handleInput(event);
    this.isKeywordHighlighted = true;
    this.phxSelectionList?.setFocusIndex(-1);
    if (this.selectedDomain) {
      this.updateCachedMessages();
    }
  }

  // Override onSearch to add domain-specific logic
  public onSearch(query: string): void {
    super.onSearch(query);
    if (this.selectedDomain) {
      this.updateCachedMessages();
    }
  }

  public onTokenRemove(token: PhxOmniOption): void {
    this.phxTokenRemove.emit(token);

    const isRemovedTokenQuickOption = this.isSelectedQuickOption(token);

    if (isRemovedTokenQuickOption) {
      this.phxSelectedQuickOptions = this.phxSelectedQuickOptions.filter((opt) => opt.value !== token.value);
      this.selectedQuickOptions = this.phxSelectedQuickOptions.map(this.transformToPhxSelectOption.bind(this));
      this.phxQuickSelectionsChange.emit(this.phxSelectedQuickOptions);
    } else {
      this.selectedOmniOptions = this.selectedOmniOptions.filter((t) => t.value !== token.value);
      this.selectedOptions = this.selectedOptions.filter((opt) => opt.value !== token.value);
    }

    this.updateCombinedSelections();

    // If dropdown is open, re-filter to show the removed item if it matches search
    if (this.isDropdownOpen) {
      this.filterSelectedOptions();
    }
  }

  public tokenRemoveWithKeyboard(token: PhxOmniOption): void {
    this.onTokenRemove(token);
    this._cdr.detectChanges();

    if (!this.isDropdownOpen) {
      return;
    }

    const items = this.focusableItems.toArray();
    if (!items.length) {
      return;
    }
    this.resetFocusState();
    const nextIndex = this.calculateNextFocusIndex(items);
    this.focusElementAtIndex(items, nextIndex);
  }

  public onShowDeactivatedEntitiesChange(show: boolean): void {
    this.phxShowDeactivatedEntitiesChange.emit(show);
  }

  private resetFocusState(): void {
    this.isKeywordHighlighted = false;
    if (this.phxSelectionList) {
      this.phxSelectionList.setFocusIndex(-1);
    }
  }

  private calculateNextFocusIndex(items: PhxOmnifilterFocusableDirective[]): number {
    let nextIndex = this._activeFocusElement;

    if (nextIndex < items.length) {
      if (nextIndex === -1) {
        nextIndex = 0;
      }
    } else {
      nextIndex = 0;
    }

    return nextIndex;
  }

  private focusElementAtIndex(items: PhxOmnifilterFocusableDirective[], index: number): void {
    this._activeFocusElement = index;

    if (items[index]?.phxOmnifilterFocusableDirective === 'selection-list') {
      this.focusSelectionList();
    } else {
      items[index]?.focus();
    }
  }

  public onSearchClear(): void {
    this.phxInputValue = '';
    this.phxSearch.emit('');
  }

  public onCrossClick(): void {
    if (this.phxDisabled) return;
    if (!this.phxMultiple) {
      if (this.combinedTokens.length > 0) {
        this.clearSelections();
      }
      this.onSearchClear();
    } else {
      this.onSearchClear();
    }
  }

  public clearSelections(): void {
    this.selectedOmniOptions = [];
    this.selectedOptions = [];
    this.phxSelectedQuickOptions = [];
    this.selectedQuickOptions = [];
    this.combinedTokens = [];
    this.filterSelectedOptions();
    this.phxClearAll.emit();
    this.emitSelectedData();
    this.omnifilterInput.nativeElement?.focus();
  }

  public get hasInput(): boolean {
    return !!this.phxInputValue?.trim();
  }

  public onDomainSelect(domain: string): void {
    this.selectedDomain = this.domainList.find((e) => e.name === domain) ?? undefined;
    this.selectedDomainItem = this.selectedDomain?.name || undefined;
    this.phxDomainChange.emit(this.selectedDomainItem);
    this.formatOptions();
    this.filterSelectedOptions();
    this.updateCachedMessages();
    this.quickOptions = this.getQuickOptions();
  }

  public registerOnChange(fn: (value: PhxSelectedOmniOptionsByDomain) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.phxDisabled = isDisabled;
  }

  public onQuickSelectionChange(selected: PhxSelectOption[] | PhxSelectOption): void {
    if (this.phxNavigateOnEntityClick) {
      this.handleNavigateOnEntityClick(selected);
      return;
    }

    let selectedOptions: PhxSelectOption[];
    if (Array.isArray(selected)) {
      if (this.phxSingleSelectPerDomain) {
        selectedOptions = this.replaceExistingDomainSelection(selected);
      } else selectedOptions = selected;
    } else selectedOptions = [selected];
    this.selectedQuickOptions = selectedOptions;
    this.phxSelectedQuickOptions = selectedOptions.map(this.transformToOmniOption);
    this.updateCombinedSelections();
    this.phxQuickSelectionsChange.emit(this.phxSelectedQuickOptions);

    // Filter out selected items from the list
    this.filterSelectedOptions();

    if (!this.phxMultiple) {
      this.updateInputValueAndCloseDropdown(this.combinedTokens[0]?.label || '');
    }
  }

  public onRegularSelectionChange(selectedOption: PhxSelectOption[] | PhxSelectOption): void {
    if (this.phxNavigateOnEntityClick) {
      this.handleNavigateOnEntityClick(selectedOption);
      return;
    }

    let selected: PhxSelectOption[];
    if (Array.isArray(selectedOption)) {
      if (this.phxSingleSelectPerDomain) {
        selected = this.replaceExistingDomainSelection(selectedOption);
      } else selected = selectedOption
    } else selected = [selectedOption];

    this.selectedOmniOptions = selected.map(this.transformToOmniOption);
    this.selectedOptions = selected;
    this.updateCombinedSelections();

    // Filter out selected items from the list
    const focusedIndex = this.phxSelectionList?.focusedIndex || 0;
    this.filterSelectedOptions();
    this.phxSelectionList?.setFocusIndex(focusedIndex);
    if (this.phxOptions.length === 0) {
      this.isKeywordHighlighted = true;
    }

    if (!this.phxMultiple) {
      this.updateInputValueAndCloseDropdown(selected[0]?.label || '');
    }
  }

  private replaceExistingDomainSelection(newOptions: PhxSelectOption[]): PhxSelectOption[] {
    const singleSelectedOptionsMap = new Map<string, PhxSelectOption>();
    for (const newOption of newOptions) {
      singleSelectedOptionsMap.set(newOption.data.domain, newOption);
    }
    return Array.from(singleSelectedOptionsMap.values());
  }

  private updateInputValueAndCloseDropdown(newInputValue: string): void {
    this.closeDropdown();
    this.updateInputValue(newInputValue);
  }

  private updateInputValue(newInputValue: string): void {
    this.searchQuery = newInputValue;
    this.phxInputValue = newInputValue;
  }

  // transformToPhxSelectOption is provided by base component

  protected formatOptions(): void {
    // Call base component's formatOptions
    super.formatOptions();
    // Cache the formatted options before filtering
    this._cachedFormattedOptions = [...this.phxOptions];
  }

  private filterSelectedOptions(): void {
    const selectedValues = this.selectedOptions.map(opt => opt.value);
    const selectedQuickValues = this.selectedQuickOptions.map(opt => opt.value);
    const combinedValues = new Set([...selectedValues, ...selectedQuickValues]);

    if (combinedValues.size === 0) {
      this.phxOptions = [...this._cachedFormattedOptions];
      this.quickOptions = [...this._cachedQuickOptions];
      return;
    };

    // Filter out selected options from the list
    this.phxOptions = this._cachedFormattedOptions.filter(option => {
      // Keep group labels
      if (option.groupLabel) {
        return true;
      }
      // Filter out selected options
      return option.value && !combinedValues.has(option.value);
    });

    // Remove empty groups (groups with no options)
    const cleanedOptions: PhxSelectOption[] = [];
    for (let i = 0; i < this.phxOptions.length; i++) {
      const currentOption = this.phxOptions[i];
      if (currentOption.groupLabel) {
        // Check if next option exists and is not a group label
        if (i + 1 < this.phxOptions.length && !this.phxOptions[i + 1].groupLabel) {
          cleanedOptions.push(currentOption);
        }
      } else {
        cleanedOptions.push(currentOption);
      }
    }
    this.phxOptions = cleanedOptions;

    // Filter quick options if they exist
    if (this._cachedQuickOptions && this._cachedQuickOptions.length > 0) {
      const filteredQuickOptions = this._cachedQuickOptions.filter(option => {
        // Keep group labels
        if (option.groupLabel) {
          return true;
        }
        // Filter out selected options
        return option.value && !combinedValues.has(option.value);
      });

      // Remove Quick Select group label if no options remain
      if (filteredQuickOptions.length > 1) {
        this.quickOptions = filteredQuickOptions;
      } else {
        this.quickOptions = [];
      }
    }

    this._cdr.detectChanges();
  }

  public createTabKeyEvent = (): KeyboardEvent => {
    return new KeyboardEvent('keydown', {
      key: 'Tab',
      code: 'Tab',
      keyCode: 9,
      bubbles: true,
      cancelable: true,
    });
  };

  // transformToOmniOption is provided by base component

  public onFindByKeywordClick(keyword: string): void {
    this.phxFindByKeywordClick.emit(keyword);
    this.closeDropdown();
  }

  public onInputKeyDown(event: KeyboardEvent): void {
    if (event.key === KeyboardKeys.ENTER && !this.isDropdownOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.openDropdown();
    }
  }

  public onInputClick(): void {
    if (!this.isDropdownOpen) {
      this.openDropdown();
    }
  }

  public get shouldShowFooter(): boolean {
    return this.phxOptions?.length > 0 && (this.shouldShowGroupByOption || this.phxShowDeactivatedToggle);
  }

  public get shouldShowGroupByOption(): boolean {
    return this.selectedDomain && this.selectedDomain.name === PhxDomainOptions.ALL;
  }

  public get hasSelections(): boolean {
    return this.selectedOptions?.length > 0;
  }

  public get isInputMatchingSelectedOption(): boolean {
    if (!this.phxInputValue || this.phxMultiple) {
      return false;
    }
    return this.combinedTokens.some(token => token.label.toLowerCase() === this.phxInputValue.toLowerCase());
  }

  private getQuickOptions(): PhxSelectOption[] {
    if (!this.selectedDomain) return [];

    let quickOptions: PhxSelectOption[] = [];

    if (this.selectedDomain.name === PhxDomainOptions.ALL) {
      // Extract quickOptions from all domain configs and combine them
      quickOptions = Object.values(this.phxDomains)
        .filter((domain) => domain.quickOptions && domain.quickOptions.length > 0)
        .reduce((acc, domain) => {
          const domainQuickOptions = domain.quickOptions.map((option) => this.transformToPhxSelectOption({ ...option, domain: domain.name }));
          return [...acc, ...domainQuickOptions];
        }, [] as PhxSelectOption[]);
    } else {
      // Extract quickOptions only from the specific selected domain
      if (this.selectedDomain.quickOptions && this.selectedDomain.quickOptions.length > 0) {
        quickOptions = this.selectedDomain.quickOptions.map((option) => this.transformToPhxSelectOption({ ...option, domain: this.selectedDomain.name }));
      }
    }

    // Add group label if there are any quick options
    if (quickOptions.length > 0) {
      quickOptions.unshift({
        groupLabel: QUICK_SELECT_GROUP_LABEL,
      });
    }

    // Cache the quick options
    this._cachedQuickOptions = quickOptions;

    return quickOptions;
  }

  private convertToDomainSelection(value: PhxOmniOption[]): PhxSelectedOmniOptionsByDomain {
    return value.reduce((acc, option) => {
      if (acc[option.domain]) {
        acc[option.domain].push(option);
      } else {
        acc[option.domain] = [option];
      }
      return acc;
    }, {} as PhxSelectedOmniOptionsByDomain);
  }

  private convertToOmniOption(value: PhxSelectedOmniOptionsByDomain): PhxOmniOption[] {
    return Object.values(value).reduce((acc, val) => acc.concat(val), []);
  }

  private updateCombinedSelections(sendOnchange: boolean = true): void {
    const combinedTokens: PhxOmniOption[] = [...this.selectedOmniOptions, ...(this.phxSelectedQuickOptions ?? [])];
    this.combinedTokens = combinedTokens;
    if (sendOnchange) this.emitSelectedData();
    this.updateTooltipContent();
    this._cdr.detectChanges();
  }

  private emitSelectedData() {
    this._onChange(this.convertToDomainSelection(this.selectedOmniOptions));
  }

  private generatePlaceholderText(): string {
    if (!this.phxDomains) {
      return this._phxTranslate.transform('Search all domains');
    }

    if (this.selectedDomain.name === PhxDomainOptions.ALL) {
      const relevantDomains: PhxDomainConfig[] = Object.values(this.phxDomains).filter((d) => d.name !== PhxDomainOptions.ALL);

      const labels: string[] = relevantDomains.filter((d) => d.label).map((d) => d.label.toLowerCase());

      if (labels.length > 0) {
        const labelsText: string = labels.join(', ');
        return labelsText.charAt(0).toUpperCase() + labelsText.slice(1);
      }
    } else {
      return `${this.selectedDomain.label} ${this.selectedDomain.searchKeys.join(' / ')}`
    }
  }

  private generateDeactivatedEntitiesMessage(): string {
    if(!this.phxDomains) {
      return this._phxTranslate.transform('Show deactivated entities');
    }
    if(this.selectedDomain.name === PhxDomainOptions.ALL) {
      return this._phxTranslate.transform('Show deactivated entities');
    }
    return `${this._phxTranslate.transform('Show deactivated')} ${this.selectedDomain.label}`;
  }

  protected updateCachedMessages(): void {
    if (this.selectedDomain) {
      super.updateCachedMessages(this.selectedDomain);
      this._cachedPlaceholderText = this.generatePlaceholderText();
      this.deactivatedEntitesMessage = this.generateDeactivatedEntitiesMessage();
    }
  }

  public onHostKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case KeyboardKeys.ESC:
        this.closeDropdown();
        this.updateCachedMessages();
        this._activeFocusElement = -1;
        this.dropdownContainer.nativeElement.blur();
        break;
      case KeyboardKeys.ARROW_DOWN:
        event.preventDefault();
        this.handleArrowNavigation('down');
        break;
      case KeyboardKeys.ARROW_UP:
        event.preventDefault();
        this.handleArrowNavigation('up');
        break;
      case KeyboardKeys.ENTER:
      case KeyboardKeys.SPACE:
        event.preventDefault();
        this.handleEnterOrSpaceKey(event);
        break;
      case KeyboardKeys.TAB:
        this.handleTabKey(event, true);
        break;
    }
    this._cdr.detectChanges();
  }

  private handleNavigateOnEntityClick(selected: PhxSelectOption[] | PhxSelectOption): void {
    const clickedOption = Array.isArray(selected) ? selected[0] : selected;

    if (clickedOption && !clickedOption.groupLabel) {
      const omniOption = this.transformToOmniOption(clickedOption);
      this.phxEntityClick.emit(omniOption);
      this.closeDropdown();
      this.onSearchClear();
    }
  }

  /**
   * Handle arrow key navigation for omnifilter.
   * When phxShowFindByKeyword is enabled and there is input:
   * - Navigation starts with keyword option highlighted
   * - Arrow down moves to selection list options
   * - Arrow up from first option moves back to keyword
   */
  private handleArrowNavigation(direction: 'up' | 'down'): void {
    const hasKeywordOption = this.phxShowFindByKeyword && this.hasInput;
    const hasOptionsList = this.phxOptions?.length > 0 || this.quickOptions?.length > 0;

    if (!hasKeywordOption && !hasOptionsList) {
      return;
    }

    if (!hasKeywordOption) {
      this.phxSelectionList.navigateFocusedIndex(direction);
      return;
    }

    if (direction === 'down') {
      const isAtLastItem = this.isAtLastSelectableItem();

      if (hasOptionsList && isAtLastItem) {
        // Wrap back to keyword
        this.isKeywordHighlighted = true;
        this.phxSelectionList.setFocusIndex(-1);
        return;
      }

      if (hasOptionsList) {
        // Move from keyword to first selection list item
        this.isKeywordHighlighted = false;
        this.phxSelectionList.navigateFocusedIndex('down');
      }
    } else {
      const isAtFirstItem = this.isAtFirstSelectableItem();

      if (hasOptionsList && isAtFirstItem) {
        this.isKeywordHighlighted = true;
        this.phxSelectionList.setFocusIndex(-1);
        return;
      }

      if (hasOptionsList) {
        this.isKeywordHighlighted = false;
        this.phxSelectionList.navigateFocusedIndex('up');
      }
    }
  }

  private isAtFirstSelectableItem(): boolean {
    if (!this.phxSelectionList) return false;
    const options = this.phxOptions;
    if (!options?.length) return false;

    const firstSelectableIndex = options.findIndex(opt => !opt.groupLabel && !opt.disabled);
    return this.phxSelectionList.focusedIndex === firstSelectableIndex;
  }

  private isAtLastSelectableItem(): boolean {
    if (!this.phxSelectionList) return false;
    const options = this.phxOptions;
    if (!options?.length) return false;

    // Find the last selectable index
    let lastSelectableIndex = -1;
    for (let i = options.length - 1; i >= 0; i--) {
      if (!options[i].groupLabel && !options[i].disabled) {
        lastSelectableIndex = i;
        break;
      }
    }
    return this.phxSelectionList.focusedIndex === lastSelectableIndex;
  }

  private handleEnterOrSpaceKey(event: KeyboardEvent): void {
    if (this.isKeywordHighlighted && this.phxShowFindByKeyword && this.hasInput) {
      this.onFindByKeywordClick(this.phxInputValue);
      return;
    }

    if (this.phxSelectionList && this.phxSelectionList.focusedIndex !== -1) {
      const options = this.phxSelectionList.options;
      const focusedOption = options?.[this.phxSelectionList.focusedIndex];
      if (focusedOption && !focusedOption.disabled && !focusedOption.groupLabel) {
        this.phxSelectionList.handleOptionClick(focusedOption, this.phxSelectionList.focusedIndex);
      }
    } else if (event.key === KeyboardKeys.ENTER && this.phxShowFindByKeyword && this.hasInput) {
      this.onFindByKeywordClick(this.phxInputValue);
    }
  }

  public onSelectionListMouseInteraction(): void {
    this.isKeywordHighlighted = false;
    this._cdr.markForCheck();
  }

  public onKeywordSearchMouseEnter(): void {
    this.isKeywordHighlighted = true;
    if (this.phxSelectionList) {
      this.phxSelectionList.setFocusIndex(-1);
    }
    this._cdr.markForCheck();
  }

  public onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case KeyboardKeys.ESC:
        this.closeDropdown();
        this.updateCachedMessages();
        this._activeFocusElement = -1;
        if (this.phxSelectionList) {
          this.phxSelectionList.setFocusIndex(-1);
        }
        this.dropdownContainer.nativeElement.blur();
        break;
      case KeyboardKeys.TAB:
        this.handleTabKey(event);
        break;
      case KeyboardKeys.ARROW_LEFT:
      case KeyboardKeys.ARROW_RIGHT:
        this.handleHorizontalArrowKey(event);
        break;
    }
    this._cdr.detectChanges();
  }

  private handleHorizontalArrowKey(event: KeyboardEvent): void {
    event.preventDefault();
    const items = this.focusableItems.toArray();
    if (!items.length) return;

    const tokenIndices = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.phxOmnifilterFocusableDirective === 'token')
      .map(({ index }) => index);

    if (!tokenIndices.length) return;

    const currentTokenIndex = tokenIndices.indexOf(this._activeFocusElement);

    switch (event.key) {
      case KeyboardKeys.ARROW_LEFT:
        if (currentTokenIndex === 0 || currentTokenIndex === -1) {
          this._activeFocusElement = tokenIndices[tokenIndices.length - 1];
        } else {
          this._activeFocusElement = tokenIndices[currentTokenIndex - 1];
        }
        items[this._activeFocusElement]?.focus();
        this.resetFocusState();
        break;
      case KeyboardKeys.ARROW_RIGHT:
        if (currentTokenIndex === tokenIndices.length - 1 || currentTokenIndex === -1) {
          this._activeFocusElement = tokenIndices[0];
        } else {
          this._activeFocusElement = tokenIndices[currentTokenIndex + 1];
        }
        items[this._activeFocusElement]?.focus();
        this.resetFocusState();
        break;
    }
  }

  private handleTabKey(event: KeyboardEvent, hostKeyDown: boolean = false): void {
    if (!this.isDropdownOpen) {
      return;
    }

    event.preventDefault();
    const items = this.focusableItems.toArray();
    this.resetFocusState();

    // If the host key is down, we need to set the active focus element to the first token if present
    if (hostKeyDown) {
      const tokens = this.focusableItems?.filter((item) => item.phxOmnifilterFocusableDirective === 'token');
      if (tokens.length) {
        this._activeFocusElement = items.length - tokens.length - 1;
      }
    }

    if (!items.length) {
      return;
    }

    const isShiftTab = event.shiftKey;
    this.updateActiveFocusIndex(isShiftTab, items.length);

    if (isShiftTab) {
      this.handleBackwardTabNavigation(items);
    } else {
      this.handleForwardTabNavigation(items);
    }
  }

  private updateActiveFocusIndex(isShiftTab: boolean, itemsLength: number): void {
    if (isShiftTab) {
      if (this._activeFocusElement <= 0) {
        this._activeFocusElement = itemsLength;
      }
    } else {
      if (this._activeFocusElement >= itemsLength - 1) {
        this._activeFocusElement = -1;
      }
    }
  }

  private handleBackwardTabNavigation(items: PhxOmnifilterFocusableDirective[]): void {
    const prevIndex = this._activeFocusElement - 1;

    if (prevIndex >= 0 && items[prevIndex].phxOmnifilterFocusableDirective === 'selection-list') {
      this._activeFocusElement = prevIndex;
      this.focusSelectionList();
    } else {
      this._activeFocusElement = prevIndex;
      items[this._activeFocusElement]?.focus();
    }
  }

  private handleForwardTabNavigation(items: PhxOmnifilterFocusableDirective[]): void {
    const nextIndex = this._activeFocusElement + 1;

    if (nextIndex < items.length && items[nextIndex].phxOmnifilterFocusableDirective === 'selection-list') {
      this._activeFocusElement = nextIndex;
      this.focusSelectionList();
    } else {
      this._activeFocusElement = nextIndex;
      items[this._activeFocusElement]?.focus();
    }
  }

  private focusSelectionList(): void {
    this.phxSelectionList?.focusVirtualScrollViewport();
    this.phxSelectionList.focusFirstItem();
  }

  private updateTooltipContent(): void {
    // return the first 8 selected option labels, joined by commas
    this._tooltipContent = this.combinedTokens.filter((_, index) => index < this.MaximumTooltipOptionsToShow).map((token) => token.label).join(', ');
  }

  private openDropdown(): void {
    this.isDropdownOpen = true;
    this.saveTriggerWidth();
    this.phxOpenChange.emit(true);
  }

  private closeDropdown(): void {
    this.isDropdownOpen = false;
    this.phxOpenChange.emit(false);
  }

  private saveTriggerWidth(): void {
    this.triggerWidth = this.dropdownTrigger?.nativeElement?.offsetWidth || 0;
  }
}
